import { backendApi } from "@/services/backend/api";
import { Textblock, List } from "@/services/backend/textblocks/types/textblock";
import { DocumentId, DocumentKind, SubdomainName } from "@bitory-gmbh/bsm";
import { UUID } from "@bitory-gmbh/nidavellir";
import { Filter } from "@/shared/lib/filter/filter";
import { providesList } from "@/services/backend/_lib/tag-helpers";

const textblockApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    textblocksTextblockCreate: build.mutation<Textblock, CreateRequest>({
      query: (request) => ({
        url: `/textblocks/textblock`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: [
        "Textblock",
        "HtzConfirmation",
        "HtzOffer",
        "HtzOrder",
        "RltConfirmation",
        "RltOffer",
        "RltOrder",
        "VbsConfirmation",
        "VbsOffer",
        "VbsOrder",
      ],
    }),
    textblocksTextblockList: build.query<List, ListRequest>({
      query: (request) => ({
        url: `/textblocks/textblock`,
        method: "GET",
        params: { request: btoa(JSON.stringify(request)) },
      }),
      providesTags: (result) => providesList(result?.items, "Textblock"),
    }),
    textblocksTextblockUpdate: build.mutation<Textblock, UpdateRequest>({
      query: (request) => ({
        url: `/textblocks/textblock/${request.id}`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: [
        "Textblock",
        "HtzConfirmation",
        "HtzOffer",
        "HtzOrder",
        "RltConfirmation",
        "RltOffer",
        "RltOrder",
        "VbsConfirmation",
        "VbsOffer",
        "VbsOrder",
      ],
    }),
    textblocksTextblockChangeOrder: build.mutation<List, ChangeOrderRequest>({
      query: (request) => ({
        url: `/textblocks/textblock/order`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: [
        "Textblock",
        "HtzConfirmation",
        "HtzOffer",
        "HtzOrder",
        "RltConfirmation",
        "RltOffer",
        "RltOrder",
        "VbsConfirmation",
        "VbsOffer",
        "VbsOrder",
      ],
    }),
    textblocksTextblockDelete: build.mutation<Textblock, DeleteRequest>({
      query: (request) => ({
        url: `/textblocks/textblock`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: [
        "Textblock",
        "HtzConfirmation",
        "HtzOffer",
        "HtzOrder",
        "RltConfirmation",
        "RltOffer",
        "RltOrder",
        "VbsConfirmation",
        "VbsOffer",
        "VbsOrder",
      ],
    }),
  }),
});

export const {
  useTextblocksTextblockCreateMutation,
  useTextblocksTextblockListQuery,
  useTextblocksTextblockUpdateMutation,
  useTextblocksTextblockChangeOrderMutation,
  useTextblocksTextblockDeleteMutation,
} = textblockApi;

export type CreateRequest = {
  id: UUID;
  subdomain: SubdomainName;
  documentKind: DocumentKind;
  documentId: DocumentId;
  title: string;
  shorthand: string;
  text: string;
};

export type ListRequest = {
  subdomain: Filter<SubdomainName>;
  documentKind: Filter<DocumentKind>;
  documentId: Filter<DocumentId>;
  offset?: number;
  limit?: number;
};

export type UpdateRequest = {
  id: UUID;
  title: string;
  shorthand: string;
  text: string;
};

export type ChangeOrderRequest = {
  subdomain: SubdomainName;
  documentKind: DocumentKind;
  documentId: DocumentId;
  id: UUID;
  order: number;
};

export type DeleteRequest = {
  ids: UUID[];
};
